import React, { useEffect, useState } from "react";
import { Box, Button, CircularProgress, Stack, TextField, Typography } from "@mui/material";
import { useDispatch } from "react-redux";
import { Fonts } from "../../constants/AppEnums";
import AppDialog from "../../components/AppBase/AppDialog";
import { showMessage } from "../../redux/actions/Common";
import { createRisOrg, updateRisOrg } from "../../redux/actions/RisAdmin";
import { theme } from "../../themes/theme";
import { RisOrg } from "../../types/models/RisOrg";

// Same rule ris-api applies: a bare domain with at least one dot
const emailSuffixPattern = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/;

export const normaliseEmailSuffix = (value: string) => value.trim().replace(/^@/, "").toLowerCase();

interface OrgDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved: (org: RisOrg) => void;
  /** The org to edit, or undefined to create one */
  org?: RisOrg;
}

const OrgDialog: React.FC<OrgDialogProps> = ({ isOpen, onClose, onSaved, org }) => {
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [name, setName] = useState<string>("");
  const [emailSuffix, setEmailSuffix] = useState<string>("");
  const [nameError, setNameError] = useState<string>();
  const [emailSuffixError, setEmailSuffixError] = useState<string>();

  useEffect(() => {
    if (isOpen) {
      setName(org?.Name ?? "");
      setEmailSuffix(org?.EmailSuffix ?? "");
      setNameError(undefined);
      setEmailSuffixError(undefined);
    }
  }, [isOpen]);

  const validateName = (value: string) => {
    const error = value.trim().length === 0 ? "Please enter the organisation name" : undefined;
    setNameError(error);
    return !error;
  };

  const validateEmailSuffix = (value: string) => {
    const normalised = normaliseEmailSuffix(value);
    const error =
      normalised.length > 0 && !emailSuffixPattern.test(normalised)
        ? "Enter a domain such as example.co.nz"
        : undefined;
    setEmailSuffixError(error);
    return !error;
  };

  const handleSubmit = () => {
    const isNameValid = validateName(name);
    const isSuffixValid = validateEmailSuffix(emailSuffix);
    if (!isNameValid || !isSuffixValid) return;

    const request = {
      Name: name.trim(),
      EmailSuffix: normaliseEmailSuffix(emailSuffix) || null,
    };
    const onResult = (saved: RisOrg | undefined) => {
      setIsSubmitting(false);
      if (saved) {
        dispatch(
          showMessage(
            org ? "The organisation has been updated." : "The organisation has been created."
          )
        );
        onSaved(saved);
      }
    };

    setIsSubmitting(true);
    if (org) {
      dispatch(updateRisOrg(org.Id, request, onResult));
    } else {
      dispatch(createRisOrg(request, onResult));
    }
  };

  return (
    <AppDialog
      open={isOpen}
      dividers
      disableBackDropOffClose
      title={
        <Typography sx={{ mb: 1, fontWeight: Fonts.SEMI_BOLD }}>
          {org ? "Edit Organisation" : "Create Organisation"}
        </Typography>
      }
      onClose={onClose}
    >
      <Box sx={{ position: "relative" }}>
        {isSubmitting && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1,
            }}
          >
            <CircularProgress size="3rem" sx={{ color: theme.palette.primary.main }} />
          </Box>
        )}
        <Box sx={{ px: 2, py: 1 }}>
          <TextField
            required
            fullWidth
            id="OrgName"
            label="Name"
            value={name}
            InputLabelProps={{ shrink: true }}
            onChange={(event) => {
              setName(event.target.value);
              validateName(event.target.value);
            }}
            error={!!nameError}
            helperText={nameError}
          />
        </Box>
        <Box sx={{ px: 2, py: 2 }}>
          <TextField
            fullWidth
            id="OrgEmailSuffix"
            label="Email suffix"
            placeholder="example.co.nz"
            value={emailSuffix}
            InputLabelProps={{ shrink: true }}
            onChange={(event) => {
              setEmailSuffix(event.target.value);
              validateEmailSuffix(event.target.value);
            }}
            error={!!emailSuffixError}
            helperText={
              emailSuffixError ??
              "Optional. When set, only emails on this domain can be added to the organisation."
            }
          />
        </Box>
        <Stack direction="row" spacing={2} sx={{ justifyContent: "flex-end", my: 2, px: 2 }}>
          <Button
            variant="contained"
            disabled={isSubmitting}
            onClick={handleSubmit}
            sx={{ minWidth: 100, color: "white", textTransform: "none" }}
          >
            {org ? "Save Changes" : "Create Organisation"}
          </Button>
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{ minWidth: 100, textTransform: "none" }}
          >
            Cancel
          </Button>
        </Stack>
      </Box>
    </AppDialog>
  );
};

export default OrgDialog;
